import { createSignal, createEffect, onMount, For, Show } from 'solid-js';
import { useActiveBot } from '../../state/activeBot';
import { useBotFanOut } from '../../hooks/useBotFanOut';
import { BOT_ACTIONS } from '../../services/resourceActions';
import ItemIdentifierPicker from '../../components/ItemIdentifierPicker';
import { useApi, isOk } from '../../api';
import { showConfirm } from '../../components/Dialog';
import { useToast } from '../../components/MessageBox';
import { useInventoryApi, type StorageInfo } from '../../services/inventoryApi';
import { useCraftingResolver } from '../../services/craftingResolverContext';
import { Citybuild } from '../../models/Citybuild';
import Text from '../../components/Text';
import LocationPicker from '../../components/LocationPicker';
import { useTranslation } from '../../hooks/useTranslation';
import BotScoped from '../../components/BotScoped';
import StorageSelect from '../../components/StorageSelect';
import styles from './UnloadArea.module.css';
import { firstError } from '../../api/envelope';

const citybuildOptions = Object.keys(Citybuild).filter(k => isNaN(Number(k))) as (keyof typeof Citybuild)[];

interface UnloadAreaConfig {
    id: string;
    name: string;
    areaCitybuild: number;
    areaCitybuildName: string;
    areaLocation: string;
    range: number;
    minFreeSlots: number;
    unloadStorageId: string;
    everythingExceptRepairable: boolean;
    itemKeys: string[];
    compress: boolean;
    dumpXp: boolean;
    isEnabled: boolean;
    createdAt: string;
}

export default function UnloadAreaPage() {
    return <BotScoped groupAware><UnloadAreaInner /></BotScoped>;
}

function UnloadAreaInner() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const resolver = useCraftingResolver();

    const [configs, setConfigs] = createSignal<UnloadAreaConfig[]>([]);
    const [storages, setStorages] = createSignal<StorageInfo[]>([]);
    const [loading, setLoading] = createSignal(true);

    const [showForm, setShowForm] = createSignal(false);
    const [editId, setEditId] = createSignal<string | null>(null);
    const [formName, setFormName] = createSignal('');
    const [formCitybuild, setFormCitybuild] = createSignal(0);
    const [formLocation, setFormLocation] = createSignal('');
    const [formRange, setFormRange] = createSignal(30);
    const [formMinFreeSlots, setFormMinFreeSlots] = createSignal(3);
    const [formStorageId, setFormStorageId] = createSignal('');
    const [formEverything, setFormEverything] = createSignal(true);
    const [formItemIdentifiers, setFormItemIdentifiers] = createSignal<string[]>([]);
    const [pendingKeys, setPendingKeys] = createSignal<string[] | null>(null);
    const [formCompress, setFormCompress] = createSignal(false);
    const [formDumpXp, setFormDumpXp] = createSignal(false);
    const [formIsEnabled, setFormIsEnabled] = createSignal(true);
    const [saving, setSaving] = createSignal(false);

    const { botId: activeBotId, targetIds } = useActiveBot();
    const fanOut = useBotFanOut();
    const botId = () => activeBotId() ?? '';
    const inv = useInventoryApi();

    function keysToIdentifiers(keys: string[]): string[] {
        const r = resolver();
        if (!r) return [];
        const items = r.getAllItems() ?? [];
        return keys
            .map(k => {
                const [idStr, dmgStr] = k.split(':');
                const id = parseInt(idStr);
                const dmg = parseInt(dmgStr) || 0;
                const def = items.find(i => i.id === id && (i.metadata ?? 0) === dmg) ?? items.find(i => i.id === id);
                return def ? `minecraft:${def.name}` : null;
            })
            .filter((x): x is string => !!x);
    }

    function identifiersToKeys(identifiers: string[]): string[] {
        const r = resolver();
        if (!r) return [];
        const items = r.getAllItems() ?? [];
        return identifiers
            .map(identifier => {
                const bare = identifier.replace(/^minecraft:/, '');
                const def = items.find(i => i.name === bare);
                return def ? `${def.id}:${def.metadata ?? 0}` : null;
            })
            .filter((x): x is string => !!x);
    }

    createEffect(() => {
        const r = resolver();
        const pk = pendingKeys();
        if (r && pk) {
            setFormItemIdentifiers(keysToIdentifiers(pk));
            setPendingKeys(null);
        }
    });

    onMount(async () => {
        try {
            const [cfgRes, storageList] = await Promise.all([
                api.inventoryUnloadConfigs.getUnloadAreaConfigs(),
                inv.fetchStorages(),
            ]);
            if (isOk(cfgRes.data)) setConfigs((cfgRes.data.value.configs ?? []) as unknown as UnloadAreaConfig[]);
            setStorages(storageList);
        } catch { /* toast below */ }
        finally { setLoading(false); }
    });

    async function loadConfigs() {
        const res = await api.inventoryUnloadConfigs.getUnloadAreaConfigs();
        if (isOk(res.data)) setConfigs((res.data.value.configs ?? []) as unknown as UnloadAreaConfig[]);
    }

    function openCreate() {
        setEditId(null);
        setFormName('');
        setFormCitybuild(0);
        setFormLocation('');
        setFormRange(30);
        setFormMinFreeSlots(3);
        setFormStorageId(storages()[0]?.id ?? '');
        setFormEverything(true);
        setFormItemIdentifiers([]);
        setPendingKeys(null);
        setFormCompress(false);
        setFormDumpXp(false);
        setFormIsEnabled(true);
        setShowForm(true);
    }

    function openEdit(c: UnloadAreaConfig) {
        setEditId(c.id);
        setFormName(c.name);
        setFormCitybuild(c.areaCitybuild);
        setFormLocation(c.areaLocation);
        setFormRange(c.range);
        setFormMinFreeSlots(c.minFreeSlots);
        setFormStorageId(c.unloadStorageId);
        setFormEverything(c.everythingExceptRepairable);
        setFormItemIdentifiers(keysToIdentifiers(c.itemKeys ?? []));
        setPendingKeys(c.itemKeys ?? []);
        setFormCompress(c.compress);
        setFormDumpXp(c.dumpXp);
        setFormIsEnabled(c.isEnabled);
        setShowForm(true);
    }

    async function save(e: Event) {
        e.preventDefault();
        if (!formStorageId()) { toast?.error(t('UNLOADAREA_SELECT_STORAGE')); return; }
        setSaving(true);
        const body = {
            name: formName().trim() || 'UnloadArea Config',
            areaCitybuild: formCitybuild(),
            areaLocation: formLocation().trim(),
            range: formRange(),
            minFreeSlots: formMinFreeSlots(),
            unloadStorageId: formStorageId(),
            everythingExceptRepairable: formEverything(),
            itemKeys: formEverything() ? [] : identifiersToKeys(formItemIdentifiers()),
            compress: formCompress(),
            dumpXp: formDumpXp(),
            isEnabled: formIsEnabled(),
        };
        try {
            const id = editId();
            const res = id
                ? await api.inventoryUnloadConfigs.updateUnloadAreaConfig(id, body)
                : await api.inventoryUnloadConfigs.createUnloadAreaConfig(body);
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('UNLOADAREA_SAVE_FAILED'));
                return;
            }
            setShowForm(false);
            toast?.info(id ? t('UNLOADAREA_UPDATED') : t('UNLOADAREA_CREATED'));
            await loadConfigs();
        } finally { setSaving(false); }
    }

    async function deleteConfig(id: string) {
        const confirmed = await showConfirm(t('UNLOADAREA_DELETE_CONFIRM'), true);
        if (!confirmed) return;
        const res = await api.inventoryUnloadConfigs.deleteUnloadAreaConfig(id);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('UNLOADAREA_DELETE_FAILED'));
            return;
        }
        toast?.info(t('UNLOADAREA_DELETED'));
        await loadConfigs();
    }

    async function runConfig(id: string) {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('UNLOADAREA_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.inventoryUnloadConfigs.runUnloadAreaConfig(id, { botId: targetBotId }),
            { success: t('UNLOADAREA_STARTED'), failure: t('UNLOADAREA_START_FAILED') });
    }

    async function stopConfig(id: string) {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('UNLOADAREA_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.inventoryUnloadConfigs.stopUnloadAreaConfig(id, { botId: targetBotId }),
            { success: t('UNLOADAREA_STOPPED'), failure: t('UNLOADAREA_STOP_FAILED') });
    }

    function storageName(id: string) {
        return storages().find(s => s.id === id)?.name ?? id.slice(0, 8) + '...';
    }

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="UNLOADAREA_TITLE"/></h1>
                <button class="btn btn-primary" onClick={openCreate}>
                    <i class="fa-solid fa-plus" /> <Text val="UNLOADAREA_NEW_CONFIG"/>
                </button>
            </div>
            <p class={styles.subtitle}><Text val="UNLOADAREA_SUBTITLE"/></p>

            <Show when={loading()}>
                <div class={styles.empty}>
                    <i class="fa-solid fa-spinner fa-spin fa-2x" />
                </div>
            </Show>

            <Show when={!loading()}>
                <Show when={showForm()}>
                    <form class={`fcard ${styles.form}`} onSubmit={save}>
                        <h2 class={styles.formTitle}>
                            {editId() ? <Text val="UNLOADAREA_EDIT"/> : <Text val="UNLOADAREA_CREATE"/>}
                        </h2>

                        <div class={styles.formGrid}>
                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="UNLOADAREA_F_NAME"/></span>
                                <input class={styles.input} value={formName()} onInput={e => setFormName(e.currentTarget.value)} placeholder={t('UNLOADAREA_NAME_PLACEHOLDER')} />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="UNLOADAREA_F_CITYBUILD"/></span>
                                <select class={styles.input} value={formCitybuild()} onChange={e => setFormCitybuild(parseInt(e.currentTarget.value))}>
                                    <For each={citybuildOptions}>{(cb) => <option value={Citybuild[cb]}>{cb}</option>}</For>
                                </select>
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="UNLOADAREA_F_LOCATION"/></span>
                                <LocationPicker value={formLocation()} onChange={setFormLocation} optional />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="UNLOADAREA_F_RANGE"/></span>
                                <input class={styles.input} type="number" min="4" max="128" value={formRange()} onInput={e => setFormRange(parseInt(e.currentTarget.value) || 30)} />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="UNLOADAREA_F_MINFREE"/></span>
                                <input class={styles.input} type="number" min="1" max="27" value={formMinFreeSlots()} onInput={e => setFormMinFreeSlots(parseInt(e.currentTarget.value) || 3)} />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="UNLOADAREA_F_STORAGE"/></span>
                                <StorageSelect
                                    storages={storages()}
                                    value={formStorageId()}
                                    onChange={setFormStorageId}
                                    placeholder={t('UNLOADAREA_SELECT_STORAGE_OPTION')}
                                />
                            </label>
                        </div>

                        <label class={styles.checkField}>
                            <input type="checkbox" checked={formEverything()} onChange={e => setFormEverything(e.currentTarget.checked)} />
                            <span><Text val="UNLOADAREA_F_EVERYTHING"/></span>
                        </label>

                        <Show when={!formEverything()}>
                            <div class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="UNLOADAREA_F_FILTER"/></span>
                                <ItemIdentifierPicker
                                    value={formItemIdentifiers()}
                                    onChange={setFormItemIdentifiers}
                                    placeholder={t('UNLOADAREA_FILTER_PLACEHOLDER')}
                                    label="items"
                                    disabled={saving()}
                                />
                            </div>
                        </Show>

                        <label class={styles.checkField}>
                            <input type="checkbox" checked={formCompress()} onChange={e => setFormCompress(e.currentTarget.checked)} />
                            <span><Text val="UNLOADAREA_F_COMPRESS"/></span>
                        </label>

                        <label class={styles.checkField}>
                            <input type="checkbox" checked={formDumpXp()} onChange={e => setFormDumpXp(e.currentTarget.checked)} />
                            <span><Text val="UNLOADAREA_F_DUMPXP"/></span>
                        </label>

                        <label class={styles.checkField}>
                            <input type="checkbox" checked={formIsEnabled()} onChange={e => setFormIsEnabled(e.currentTarget.checked)} />
                            <span><Text val="UNLOADAREA_F_ENABLED"/></span>
                        </label>

                        <div class={styles.formActions}>
                            <button type="button" class="btn btn-secondary" onClick={() => setShowForm(false)} disabled={saving()}>
                                <Text val="CANCEL"/>
                            </button>
                            <button type="submit" class="btn btn-primary" disabled={saving() || !formStorageId()}>
                                <Show when={saving()} fallback={<><i class="fa-solid fa-floppy-disk" /> <Text val="SAVE"/></>}>
                                    <i class="fa-solid fa-spinner fa-spin" /> <Text val="SAVE"/>
                                </Show>
                            </button>
                        </div>
                    </form>
                </Show>

                <Show when={configs().length === 0 && !showForm()}>
                    <div class={styles.empty}>
                        <i class="fa-solid fa-tower-broadcast" />
                        <p><Text val="UNLOADAREA_EMPTY"/></p>
                    </div>
                </Show>

                <div class={styles.grid}>
                    <For each={configs()}>
                        {(c) => (
                            <div class={`fcard ${styles.card} ${!c.isEnabled ? styles.cardDisabled : ''}`}>
                                <div class={styles.cardHeader}>
                                    <div>
                                        <div class={styles.cardName}>{c.name}</div>
                                        <div class={styles.cardMeta}>
                                            {c.areaCitybuildName}
                                            {c.areaLocation ? ` · ${c.areaLocation}` : ''}
                                        </div>
                                    </div>
                                    <span class={c.isEnabled ? styles.pillEnabled : styles.pillDisabled}>
                                        {c.isEnabled ? <Text val="UNLOADAREA_ENABLED"/> : <Text val="UNLOADAREA_DISABLED"/>}
                                    </span>
                                </div>

                                <div class={styles.cardBody}>
                                    <div class={styles.infoRow}><i class="fa-solid fa-magnifying-glass" /> {t('UNLOADAREA_INFO_RANGE', String(c.range))}</div>
                                    <div class={styles.infoRow}><i class="fa-solid fa-box-open" /> {t('UNLOADAREA_INFO_MINFREE', String(c.minFreeSlots))}</div>
                                    <div class={styles.infoRow}><i class="fa-solid fa-warehouse" /> {storageName(c.unloadStorageId)}</div>
                                    <div class={styles.infoRow}>
                                        <i class="fa-solid fa-filter" />
                                        {c.everythingExceptRepairable
                                            ? t('UNLOADAREA_INFO_ALL_NONREPAIRABLE')
                                            : t('UNLOADAREA_INFO_ITEM_COUNT', String(c.itemKeys?.length ?? 0))}
                                    </div>
                                    <Show when={c.compress}>
                                        <div class={styles.infoRow}><i class="fa-solid fa-compress" /> <Text val="UNLOADAREA_INFO_COMPRESS"/></div>
                                    </Show>
                                    <Show when={c.dumpXp}>
                                        <div class={styles.infoRow}><i class="fa-solid fa-bolt" /> <Text val="UNLOADAREA_INFO_DUMPXP"/></div>
                                    </Show>
                                </div>

                                <div class={styles.cardActions}>
                                    <button class="btn btn-primary" onClick={() => runConfig(c.id)} disabled={!botId()} title={t('RUN')}>
                                        <i class="fa-solid fa-play" /> <Text val="UNLOADAREA_RUN"/>
                                    </button>
                                    <button class="btn btn-secondary" onClick={() => stopConfig(c.id)} disabled={!botId()} title={t('STOP')}>
                                        <i class="fa-solid fa-stop" />
                                    </button>
                                    <button class="btn btn-secondary" onClick={() => openEdit(c)} title={t('EDIT')}>
                                        <i class="fa-solid fa-pen" />
                                    </button>
                                    <button class="btn btn-danger" onClick={() => deleteConfig(c.id)} title={t('DELETE')}>
                                        <i class="fa-solid fa-trash" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </For>
                </div>
            </Show>
        </div>
    );
}
